import { Helmet } from 'react-helmet-async'
import { Alert } from '../../components/atoms/Alert'
import { Badge } from '../../components/atoms/Badge'
import { Spinner } from '../../components/atoms/Spinner'
import { Paginator } from '../../components/molecules/Paginator'
import { useAccountReturns, type AccountReturnDto } from '../../../viewmodels/account/useAccountReturns'
import { Stagger, StaggerItem } from '../../motion'

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function statusTone(status: string): 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'accepted':
    case 'processed':
      return 'success'
    case 'pending':
      return 'warning'
    case 'rejected':
      return 'error'
    default:
      return 'info'
  }
}

/** `/account/returns` — the customer's own return requests
 *  (Kiran 2026-06-15). Read-only v1; the underlying return-creation
 *  flow is a later slice. */
export function AccountReturnsPage(): JSX.Element {
  const { returns, total, page, pageSize, isLoading, error, setPage } = useAccountReturns()

  if (isLoading && returns.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Returns — Uyyamadilu</title>
      </Helmet>
      <h1 className="font-heading text-h1 text-charcoal mb-6">Returns</h1>

      {error !== null ? (
        <Alert tone="error">{error}</Alert>
      ) : returns.length === 0 ? (
        <p className="font-body text-body text-text-secondary">
          You haven&apos;t requested any returns yet.
        </p>
      ) : (
        <>
          <Stagger as="ul" className="space-y-3" amount={0.05}>
            {returns.map((r: AccountReturnDto) => (
              <StaggerItem as="li" key={r.id}>
                <div className="block rounded-xl border border-border p-5 bg-bg-primary">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-heading text-h3 text-charcoal">
                        Return on {formatDate(r.requested_at)}
                      </p>
                      <p className="font-body text-body-sm text-text-secondary mt-1">
                        Order <span className="font-mono">{r.order_id.slice(0, 8)}</span>
                        {r.organisation_name !== null ? ` · ${r.organisation_name}` : ''}
                      </p>
                    </div>
                    <Badge tone={statusTone(r.status)}>{r.status}</Badge>
                  </div>
                  <p className="font-body text-body text-charcoal mt-3 whitespace-pre-wrap">
                    {r.reason}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <div className="mt-6">
            <Paginator
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              disabled={isLoading}
            />
          </div>
        </>
      )}
    </>
  )
}
